/**
 * Turns any failure from the Laravel API into a single readable sentence.
 *
 * The API returns three different error shapes:
 *   422 -> { error: { email: ["The email field is required."], ... } }
 *   401 -> { error: "User not found" }
 *   401 -> { error: "Authentication failed", message: "..." }
 */
export function toMessage(error, fallback) {
  const fallbackMessage = fallback || 'Something went wrong. Please try again.';

  if (!error) return fallbackMessage;

  // Thrown by hand (e.g. delete-account returning success: false).
  if (!error.response) {
    if (error.code === 'ECONNABORTED') {
      return 'The request timed out. Please check your connection and try again.';
    }
    if (error.message === 'Network Error') {
      return 'Could not reach the server. Please check your connection and try again.';
    }
    return error.message || fallbackMessage;
  }

  const data = error.response.data;

  if (!data) return fallbackMessage;

  if (typeof data.error === 'string') {
    return data.error;
  }

  if (data.error && typeof data.error === 'object') {
    const fields = Object.keys(data.error);
    if (fields.length) {
      const first = data.error[fields[0]];
      return Array.isArray(first) ? first[0] : String(first);
    }
  }

  if (typeof data.message === 'string' && data.message) {
    return data.message;
  }

  if (error.response.status === 401) {
    return 'Your session has expired. Please sign in again.';
  }

  return fallbackMessage;
}
